import { fmtMsg, getServiceMethodKey, GLServiceBase, HttpMethod, ServerExceptionCode, ServiceRoute } from "commonui";
import {
	ILessonPlanListModel,
	ILessonPlanModel,
	ILessonPlanVersionModel,
	ILessonPlanTemplateModel,
	IContentConversationRepModel,
	ILessonPlanVersionSimpleModel
} from "@/models/lesson-plan";
import { IMasterPlan4DBModel } from "@/models/master-plan";
import { LessonPlanVersionStatus } from "@/util/enum";
import { ILessonPlanVersionService } from ".";
import { ILessonPlanListService, ILessonPlanTemplateService } from "./interface";
import {
	FrontMatterURLDeleteModel,
	FrontMatterURLRequestModel,
	FrontMatterURLUploadModel,
	LessonPlanExpConvCountModel,
	SignatureResoureModel
} from "./model";
import { LessonPlanLocale } from "@/locales/localeid";

class LessonPlanVersionServiceClass extends GLServiceBase<any, any> implements ILessonPlanVersionService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/lessonplanversions" };

	errMsgs = (() => {
		const result = {};
		result[
			getServiceMethodKey(this, HttpMethod.Put, ServerExceptionCode.RepLessonPlanDuplicationException)
		] = fmtMsg(LessonPlanLocale.LessonPlanListCopyFailMsg);
		result[getServiceMethodKey(this, HttpMethod.Delete, ServerExceptionCode.FrontMatterDeleteException)] = fmtMsg(
			LessonPlanLocale.FrontMatterDeleteFailureMessage
		);
		result[getServiceMethodKey(this, HttpMethod.Put, ServerExceptionCode.FrontMatterNotUploadedException)] = fmtMsg(
			LessonPlanLocale.FrontMatterUploadFailedMessage
		);

		return result;
	})();

	getLessonPlanVersions(versionGroupIds: string[], methodologyType: number): Promise<ILessonPlanVersionModel[]> {
		return this.get({ versionGroupIds, methodologyType });
	}

	getLessonPlanVersion(lessonPlanVersionId: string): Promise<ILessonPlanVersionModel> {
		return this.get("{lessonPlanVersionId}", null, { lessonPlanVersionId });
	}

	getFrontMatterURL(params: FrontMatterURLRequestModel) {
		return this.get("{lessonPlanVersionId}/units/{unitId}/frontmatter", null, params);
	}

	putFrontMatterURL(body: FrontMatterURLUploadModel, routeData) {
		return this.update("{lessonPlanVersionId}/units/{unitId}/frontmatter", body, routeData);
	}

	deleteFrontMatterURL(params: FrontMatterURLDeleteModel) {
		return this.delete("{lessonPlanVersionId}/units/{unitId}/frontmatter", null, params);
	}

	getUploadSasURL(): Promise<SignatureResoureModel[]> {
		return this.get("uploadsasurl");
	}

	getDownloadSasURL(): Promise<SignatureResoureModel[]> {
		return this.get("downloadsasurl");
	}

	createLessonPlanVersion(model: ILessonPlanVersionModel): Promise<string> {
		return this.create(model);
	}

	duplicate(model: ILessonPlanVersionModel): Promise<string> {
		return this.update("{lessonPlanVersionId}/duplicating", model, { lessonPlanVersionId: model.id });
	}

	editLessonPlanVersion(model: ILessonPlanVersionModel): Promise<boolean> {
		return this.update(model);
	}

	IsTenLessonsForDiffUnitsREP(lessonPlanVersionId: string): Promise<boolean> {
		return this.get("{lessonPlanVersionId}/canpublished", null, { lessonPlanVersionId });
	}

	ChangeLessonPlanVersionStatus(
		lessonPlanVersionId: string,
		status: LessonPlanVersionStatus,
		isStoredProcedure: boolean
	): Promise<boolean> {
		var query = isStoredProcedure ? `?isStoredProcedure=${isStoredProcedure}` : "";
		return this.update("{lessonPlanVersionId}/statuses/{status}" + query, null, { lessonPlanVersionId, status });
	}

	deleteLessonPlanVersion(lessonPlanVersionId: string, versionGroupName: string): Promise<boolean> {
		var query = `?versionGroupName=${versionGroupName}`;
		return this.delete("{lessonPlanVersionId}" + query, null, { lessonPlanVersionId });
	}

	getMasterPlans(lessonPlanVersionId: string, unitId: number): Promise<IMasterPlan4DBModel[]> {
		return this.get("{lessonPlanVersionId}/masterplans/{unitId}", null, { lessonPlanVersionId, unitId });
	}

	saveMasterPlans(lessonPlanVersionId: string, unitId: number, masterPlans: IMasterPlan4DBModel): Promise<boolean> {
		return this.update("{lessonPlanVersionId}/masterplans/{unitId}", masterPlans, { lessonPlanVersionId, unitId });
	}

	getLessonPlanVersionsByTypes(
		versionGroupIds: string[],
		methodologyTypes: number[],
		includeOnlyUnpublished: boolean = false
	): Promise<ILessonPlanVersionSimpleModel[]> {
		return this.get("getlessonplanversions", { versionGroupIds, methodologyTypes, includeOnlyUnpublished });
	}

	copyUnitLessonPlan(
		sourceLessonPlanVersionId: string,
		targetLessonPlanVersionId: string,
		unitId: number
	): Promise<void> {
		return this.update("copyunitlessonplan/{sourceLessonPlanVersionId}/{targetLessonPlanVersionId}/{unitId}", {
			sourceLessonPlanVersionId,
			targetLessonPlanVersionId,
			unitId
		});
	}
}

class LessonPlanListServiceClass extends GLServiceBase<any, any> implements ILessonPlanListService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/lessonplans" };

	getLessonPlanStatistics(lessonPlanVersionId: string, unitId: number): Promise<ILessonPlanListModel[]> {
		return this.get("statistics", { lessonPlanVersionId, unitId });
	}

	getLessonPlan(lessonPlanId: string): Promise<any> {
		return this.get("{lessonPlanId}", null, { lessonPlanId });
	}

	getLessonPlanDetails(lessonPlanId: string, lessonPlanVersionId: string, unitId: number): Promise<ILessonPlanModel> {
		return this.get("{lessonPlanId}/details", { lessonPlanId, lessonPlanVersionId, unitId }, { lessonPlanId });
	}

	createLessonPlan(model: ILessonPlanListModel): Promise<{ id: string }> {
		return this.create(model);
	}

	createLessonPlans(model: ILessonPlanListModel[]): Promise<string[]> {
		return this.create("lessonPlans", model);
	}

	deleteLessonPlan(lessonPlanId: string): Promise<boolean> {
		return this.delete("{lessonPlanId}", null, { lessonPlanId });
	}

	swapLessonPlans(preLessonPlanId: string, nextLessonPlanId: string): Promise<void> {
		return this.update("{preLessonPlanId}/swaping/{nextLessonPlanId}", null, { preLessonPlanId, nextLessonPlanId });
	}

	swapLessonPlanItems(
		lessonPlanId: string,
		preLessonPlanItemId: string,
		nextLessonPlanItemId: string
	): Promise<void> {
		return this.update(
			"{lessonPlanId}/lessonPlanItems/{preLessonPlanItemId}/swaping/{nextLessonPlanItemId}",
			null,
			{ lessonPlanId, preLessonPlanItemId, nextLessonPlanItemId }
		);
	}

	getLessonPlans(lessonPlanVersionId: string, unitId: number): Promise<any> {
		return this.get({ lessonPlanVersionId, unitId });
	}

	getRelatedVpcCount(lessonPlanVersionId: string, unitId: number): Promise<any> {
		return this.get("relatedvpccounts", { lessonPlanVersionId, unitId });
	}

	exportVpcOverviewPDF(
		versionGroupId: string,
		fileName: string,
		lessonPlanVersionId: string,
		unitId: number
	): Promise<any> {
		return this.getBlob("vpcoverviews/pdf", {
			versionGroupId,
			fileName,
			lessonPlanVersionId,
			unitId
		});
	}

	exportVpcOverviewExcel(
		versionGroupId: string,
		fileName: string,
		lessonPlanVersionId: string,
		unitId: number
	): Promise<any> {
		return this.getBlob("vpcoverviews/excel", {
			versionGroupId,
			fileName,
			lessonPlanVersionId,
			unitId
		});
	}

	getExposureConversationCount(lessonPlanVersionId: string, unitId: number): Promise<LessonPlanExpConvCountModel[]> {
		return this.get("expconvcount/{lessonPlanVersionId}/units/{unitId}", null, {
			lessonPlanVersionId,
			unitId
		});
	}

	getRepConversations(unitId: number, versionGroupId: string): Promise<IContentConversationRepModel[]> {
		return this.get("repconversations", { unitId, versionGroupId });
	}

	exportLessonPlan2PDF(
		templateId: string,
		lessonPlanVersionId: string,
		unitId: number,
		sequenceId: number | undefined,
		fileName: string | undefined
	): Promise<any> {
		return this.getBlob("downloadlessonplans/pdf", {
			templateId,
			lessonPlanVersionId,
			unitId,
			fileName,
			sequenceId
		});
	}
}

class LessonPlanTemplateServiceClass extends GLServiceBase<any, any> implements ILessonPlanTemplateService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/lessonplantemplate" };

	getLessonPlanTemplates(): Promise<ILessonPlanTemplateModel[]> {
		return this.get();
	}

	createLessonPlanTemplate(model: ILessonPlanTemplateModel): Promise<string> {
		return this.create(model);
	}

	getLessonPlanTemplate(lessonPlanTemplateId: string): Promise<ILessonPlanTemplateModel> {
		return this.get("{lessonPlanTemplateId}", null, { lessonPlanTemplateId });
	}

	editLessonPlanTemplate(lessonPlanTemplateId: string, model: ILessonPlanTemplateModel): Promise<boolean> {
		return this.update("{lessonPlanTemplateId}", model, { lessonPlanTemplateId });
	}

	deleteLessonPlanTemplate(lessonPlanTemplateId: string): Promise<boolean> {
		return this.delete("{lessonPlanTemplateId}", null, { lessonPlanTemplateId });
	}

	getUploadSasURL(): Promise<SignatureResoureModel> {
		return this.get("uploadsasurl");
	}

	getDownloadSasURL(): Promise<SignatureResoureModel> {
		return this.get("downloadsasurl");
	}
}

export const LessonPlanVersionService: () => ILessonPlanVersionService = () => new LessonPlanVersionServiceClass();

export const LessonPlanListService: () => ILessonPlanListService = () => new LessonPlanListServiceClass();

export const LessonPlanTemplateService: () => ILessonPlanTemplateService = () => new LessonPlanTemplateServiceClass();
